/**
 * GuitarFinds — Collection Browser
 * Tabs switch between Liquid-rendered panels that use Shopify/Dawn native card-product.
 * No products.json card templating, so quick add, vendor, ratings, badges, and card settings stay native.
 */
(function () {
  'use strict';

  class CollectionBrowser {
    constructor(el) {
      this.tabs = Array.from(el.querySelectorAll('[data-cb-tab]'));
      this.panels = Array.from(el.querySelectorAll('[data-cb-panel]'));
      this.seeMore = el.querySelector('[data-cb-see-more]');
      this.abortController = new AbortController();

      if (!this.tabs.length || !this.panels.length) return;

      this._bindTabs();
      this._switchTab(this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || this.tabs[0], true);
    }

    _bindTabs() {
      for (const tab of this.tabs) {
        tab.addEventListener('click', () => this._switchTab(tab), { signal: this.abortController.signal });

        tab.addEventListener(
          'keydown',
          (event) => {
            const index = this.tabs.indexOf(tab);

            const target = {
              ArrowRight: (index + 1) % this.tabs.length,
              ArrowLeft: (index - 1 + this.tabs.length) % this.tabs.length,
              Home: 0,
              End: this.tabs.length - 1
            }[event.key];

            if (target !== undefined) {
              event.preventDefault();
              this.tabs[target].focus();
            }
          },
          { signal: this.abortController.signal }
        );
      }
    }

    _switchTab(tab, isInitialLoad = false) {
      if (!tab) return;

      const panelId = tab.getAttribute('aria-controls');

      for (const candidate of this.tabs) {
        const active = candidate === tab;
        candidate.classList.toggle('collection-browser__tab--active', active);
        candidate.setAttribute('aria-selected', String(active));
        candidate.setAttribute('tabindex', active ? '0' : '-1');
      }

      for (const panel of this.panels) {
        const active = panel.id === panelId;
        if (active) {
          this._activatePanel(panel);
        } else {
          this._parkPanel(panel);
        }
        panel.hidden = !active;
        panel.setAttribute('aria-hidden', String(!active));
      }

      if (!isInitialLoad) {
        tab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      }

      if (this.seeMore) {
        const label = tab.dataset.cbSeeMoreLabel || tab.dataset.cbLabel || tab.textContent.trim();
        this.seeMore.href = tab.dataset.cbUrl || '#';
        this.seeMore.firstChild.textContent = `${label} `;
      }
    }

    _activatePanel(panel) {
      const cache = panel.querySelector('[data-cb-panel-cache]');
      if (cache) {
        panel.appendChild(cache.content);
        cache.remove();
        return;
      }

      this._hydratePanel(panel);
    }

    _parkPanel(panel) {
      if (panel.dataset.cbLoaded !== 'true' || panel.querySelector('[data-cb-panel-cache]')) return;

      const cache = document.createElement('template');
      cache.setAttribute('data-cb-panel-cache', '');
      while (panel.firstChild) cache.content.appendChild(panel.firstChild);
      panel.appendChild(cache);
    }

    _hydratePanel(panel) {
      if (panel.dataset.cbLoaded === 'true' || !panel.dataset.cbPanelUrl || panel._cbRequest) return;

      const request = new XMLHttpRequest();
      panel._cbRequest = request;
      panel.setAttribute('aria-busy', 'true');
      request.open('GET', panel.dataset.cbPanelUrl);
      request.responseType = 'document';

      const fallback = () => window.location.assign(panel.dataset.cbFallbackUrl);

      request.onload = () => {
        const content = request.response?.querySelector('[data-cb-panel-content]');
        if (request.status < 200 || request.status >= 300 || !content || new URL(request.responseURL).origin !== window.location.origin) {
          fallback();
          return;
        }

        panel.replaceChildren(...content.childNodes);
        panel.dataset.cbLoaded = 'true';
        if (panel.hidden) this._parkPanel(panel);
      };
      request.onerror = fallback;
      request.onloadend = () => {
        panel.removeAttribute('aria-busy');
        delete panel._cbRequest;
      };
      request.send();
    }

    destroy() {
      this.abortController.abort();
      for (const panel of this.panels) panel._cbRequest?.abort();
    }
  }

  function init(root = document) {
    root.querySelectorAll('[data-collection-browser]').forEach((el) => {
      if (!el._collectionBrowser) el._collectionBrowser = new CollectionBrowser(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));

  document.addEventListener('shopify:section:unload', (event) => {
    const el = event.target.querySelector('[data-collection-browser]');
    if (!el?._collectionBrowser) return;
    el._collectionBrowser.destroy();
    delete el._collectionBrowser;
  });
})();
